import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { SERVICES } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", preferred_date: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-heading text-xs tracking-widest text-gold-soft">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

export default function RequestForm({ service, setService }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!service) return toast.error("Please choose a service.");
    setLoading(true);
    try {
      await axios.post(`${API}/requests`, { ...form, service, phone: form.phone || null, preferred_date: form.preferred_date || null });
      setSent(true); setForm(EMPTY); setService("");
    } catch {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally { setLoading(false); }
  };

  return (
    <section id="request" className="px-6 md:px-16 py-24 md:py-32" data-testid="request-section">
      <Reveal className="max-w-3xl mx-auto glass rounded-[2rem] p-8 md:p-14">
        {sent ? (
          <div className="text-center py-10" data-testid="request-success">
            <CheckCircle2 className="mx-auto text-gold" size={56} strokeWidth={1.2} />
            <h2 className="font-display text-3xl gold-text mt-6">Your request was received</h2>
            <p className="mt-4 text-[#f7f1e1]/80">Thank you, beloved. Queen Boyce will reach out to you soon.</p>
            <button onClick={() => setSent(false)} data-testid="request-another-btn" className="mt-8 font-heading text-xs tracking-widest text-gold underline underline-offset-8">SEND ANOTHER REQUEST</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="request-form">
            <div>
              <p className="font-heading tracking-[0.35em] text-xs text-gold">BEGIN YOUR HEALING</p>
              <h2 className="font-display text-3xl md:text-4xl gold-text mt-3">Request a Session</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="NAME"><input required className="field" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="request-name-input" /></Field>
              <Field label="EMAIL"><input required type="email" className="field" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="request-email-input" /></Field>
              <Field label="PHONE (OPTIONAL)"><input className="field" value={form.phone} onChange={set("phone")} placeholder="Your phone" data-testid="request-phone-input" /></Field>
              <Field label="PREFERRED DATE"><input type="date" className="field" value={form.preferred_date} onChange={set("preferred_date")} data-testid="request-date-input" /></Field>
            </div>
            <Field label="SERVICE">
              <select required className="field" value={service} onChange={(e) => setService(e.target.value)} data-testid="request-service-select">
                <option value="">Choose a service</option>
                {SERVICES.map((s) => <option key={s.key} value={s.key}>{s.key}</option>)}
              </select>
            </Field>
            <Field label="MESSAGE"><textarea required rows={5} className="field resize-none" value={form.message} onChange={set("message")} placeholder="Share what is on your heart..." data-testid="request-message-input" /></Field>
            <button type="submit" disabled={loading} data-testid="request-submit-btn" className="btn-gold w-full md:w-auto rounded-full px-10 py-4 font-heading text-sm tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="animate-spin" size={16} />} SEND REQUEST
            </button>
          </form>
        )}
      </Reveal>
    </section>
  );
}
